import { createElement, lazy, useState, type ComponentType } from 'react';
import type { SubscribePricingSectionProps } from '@/features/subscribe';

function createHomepageSection<Props extends object>(
  importSection: () => Promise<{ default: ComponentType<Props> }>
) {
  let ready: ComponentType<Props> | undefined;
  let pending: ReturnType<typeof importSection> | undefined;
  const preload = () => {
    pending ??= importSection().then(
      (module) => {
        ready = module.default;
        return module;
      },
      (error: unknown) => {
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };
  const Deferred = lazy(preload);
  function Section(props: Props) {
    // Prepared client navigations render synchronously. Keep the chosen type
    // stable so later prefetches never remount a section someone is using.
    const [Content] = useState(() => ready ?? Deferred);
    return createElement(Content, props);
  }
  return { Section, preload };
}

const howItWorks = createHomepageSection(() => import('./sections/HowAtrivialWorks'));
const pricing = createHomepageSection<SubscribePricingSectionProps>(() =>
  import('@/features/subscribe').then(({ SubscribePricingSection }) => ({
    default: SubscribePricingSection,
  }))
);
const portalPreview = createHomepageSection(() => import('./sections/PortalPreview'));
const finalCTA = createHomepageSection(() => import('./sections/FinalCTA'));

export const HowAtrivialWorks = howItWorks.Section;
export const SubscribePricingSection = pricing.Section;
export const PortalPreview = portalPreview.Section;
export const FinalCTA = finalCTA.Section;

/** Client navigations prepare the complete page before replacing the current route. */
export async function preloadHomepageSections() {
  await Promise.all([
    howItWorks.preload(),
    pricing.preload(),
    portalPreview.preload(),
    finalCTA.preload(),
  ]);
}
